import React, { useState } from 'react'
import styled from 'styled-components'
import { colors } from '../../../Shared/colors'
import { useGetAllProjectsQuery } from '../../../features/projectSlice'
import { Toaster, toast } from 'react-hot-toast'

export const TodoListFirst = ({ onFilterChange }) => {
  const [filters, setFilters] = useState({
    task: '',
    project: '',
    phoneNumber: '',
    leadID: '',
    classification: ''
  })

  const { data: projects } = useGetAllProjectsQuery()

  // Next task options (matching exact backend values)
  const taskOptions = [
    'doNothing',
    'followUp',
    'contractedClient',
    'meeting',
    'receiveTokenPayment',
    'receivePartialDownPayment',
    'receiveCompleteDownPayment',
    'arrangeMeeting'
  ]

  const handleChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSearch = () => {
    // Filter out empty values
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '')
    )
    
    onFilterChange(activeFilters)
    toast.success('Filters applied successfully!')
  }

  const handleClear = () => {
    setFilters({
      task: '',
      project: '',
      phoneNumber: '',
      leadID: '',
      classification: ''
    })
    onFilterChange({})
    toast.success('Filters cleared!')
  }

  return (
    <Main>
        <Search>
            <Heading>
                ToDo List
            </Heading>
            <SubHeading>
                Filter Tasks
            </SubHeading>
            <SearchSection1>
                <InputSection>
                    <h3>Task:</h3>
                    <SelectContainer>
                        <select 
                            name="task" 
                            id="task"
                            value={filters.task}
                            onChange={handleChange}
                        >
                            <option value="">Select Task</option>
                            {taskOptions.map(task => (
                                <option key={task} value={task}>{task}</option>
                            ))}
                        </select>
                        <SelectIcon>▼</SelectIcon>
                    </SelectContainer>
                </InputSection>

                <InputSection>
                    <h3>Project:</h3>
                    <SelectContainer>
                        <select 
                            name="project" 
                            id="project"
                            value={filters.project}
                            onChange={handleChange}
                        >
                            <option value="">Select Project</option>
                            {projects?.map(project => (
                                <option key={project._id} value={project._id}>
                                    {project.projectName}
                                </option>
                            ))}
                        </select>
                        <SelectIcon>▼</SelectIcon>
                    </SelectContainer>
                </InputSection>
            </SearchSection1>
            
            <SearchSection2>
                <InputSection>
                    <h3>Phone Number:</h3>
                    <input 
                        type="tel" 
                        name="phoneNumber" 
                        id="phoneNumber" 
                        placeholder='Enter phone number'
                        value={filters.phoneNumber}
                        onChange={handleChange}
                    />
                </InputSection>

                <InputSection>
                    <h3>Lead ID:</h3>
                    <input 
                        type="number" 
                        name="leadID" 
                        id="leadID" 
                        placeholder='Enter Lead ID'
                        value={filters.leadID}
                        onChange={handleChange}
                    />
                </InputSection>

                <InputSection>
                    <h3>Classification:</h3>
                    <SelectContainer>
                        <select 
                            name="classification" 
                            id="classification"
                            value={filters.classification}
                            onChange={handleChange}
                        >
                            <option value="">Select Classification</option>
                            <option value="veryHot">Very Hot</option>
                            <option value="hot">Hot</option>
                            <option value="moderate">Moderate</option>
                            <option value="cold">Cold</option>
                            <option value="veryCold">Very Cold</option>
                        </select>
                        <SelectIcon>▼</SelectIcon>
                    </SelectContainer>
                </InputSection>
            </SearchSection2>

            <ButtonSection>
                <Button onClick={handleSearch}>
                    Apply Filters
                </Button>
                <ClearButton onClick={handleClear}>
                    Clear Filters
                </ClearButton>
            </ButtonSection>
        </Search>
        <Toaster />
    </Main>
  )
}


const Main = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  background: ${colors.white};
  border-radius: 24px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.brown}, ${colors.textBlue});
    z-index: 1;
  }
`

const Search = styled.div`
  background: linear-gradient(135deg, ${colors.lightWhite} 0%, ${colors.white} 100%);
  width: 100%;
  border-radius: 0;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const Heading = styled.h4`
  color: ${colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: linear-gradient(135deg, ${colors.brown}, ${colors.textBlue});
    border-radius: 2px;
  }
`

const SubHeading = styled.h4`
  color: ${colors.textMed};
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${colors.inputBorderWhite};
`

const SearchSection1 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`

const SearchSection2 = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  align-items: end;
`

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${colors.inputBorderWhite};
`


const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  h3 {
    color: ${colors.text};
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  div {
    position: relative;
    border: 1px solid ${colors.inputBorderWhite};
    border-radius: 8px;
    background-color: ${colors.white};
    transition: all 0.2s ease;
    
    &:hover {
      border-color: ${colors.brown};
    }
    
    &:focus-within {
      border-color: ${colors.brown};
      box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
    }

    select {
      background-color: transparent;
      width: 100%;
      padding: 0.75rem 2.5rem 0.75rem 1rem;
      outline: none;
      border: none;
      font-size: 0.875rem;
      color: ${colors.text};
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      
      &:focus {
        outline: none;
      }
    }
  }
  
  input {
    border: 1px solid ${colors.inputBorderWhite};
    border-radius: 8px;
    background-color: ${colors.white};
    width: 100%;
    padding: 0.75rem 1rem;
    outline: none;
    font-size: 0.875rem;
    color: ${colors.text};
    transition: all 0.2s ease;
    
    &:hover {
      border-color: ${colors.brown};
    }
    
    &:focus {
      border-color: ${colors.brown};
      box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
    }
    
    &::placeholder {
      color: ${colors.textMed};
    }
  }
`

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${colors.white};
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  min-width: 120px;
  height: 40px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(36, 147, 147, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(36, 147, 147, 0.4);
    background: linear-gradient(135deg, #1a6b6b 0%, ${colors.brown} 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${colors.text};
  background: ${colors.lightWhite};
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  min-width: 120px;
  height: 40px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.inputBorderWhite};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const SelectContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  background-color: ${colors.white};
  transition: all 0.2s ease;
  overflow: hidden;
  
  &:hover {
    border-color: ${colors.brown};
  }
  
  &:focus-within {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 2px rgba(36, 147, 147, 0.1);
  }
`

const SelectIcon = styled.span`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.textMed};
  font-size: 0.75rem;
  pointer-events: none;
  transition: all 0.2s ease;
  
  ${SelectContainer}:hover & {
    color: ${colors.brown};
  }
  
  ${SelectContainer}:focus-within & {
    color: ${colors.brown};
    transform: translateY(-50%) rotate(180deg);
  }
`